import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ControlComponent,
  GeoJSONSourceComponent,
  LayerComponent,
  MapComponent,
  MarkerComponent,
} from '@maplibre/ngx-maplibre-gl';
import {
  BarometerMarkerData,
  BarometerReadingView,
  CopyableTextComponent,
  IntegratorMultiSelectComponent,
  MapAntennaMarkerComponent,
  MapBarometerMarkerComponent,
  MapGatewayMarkerComponent,
  MapTrackerMobileMarkerComponent,
} from '@wheere-front/shared-ui';
import { calculateDynamicPadding, chartCustomColors, getFitBounds } from '@wheere-front/shared-util';
import { mergeBaroMeasure, parseBaroMeasure } from '@wheere-front/domain-trackers';
import { geoToModel } from '../site-model/site-model-georef';
import { timestampMs } from '../site-model/person-planner';
import { Antenna as ApiSiteAntenna, Gateway, Tracker } from '@wheere/api-site';
import { Antenna as ApiGwAntenna } from '@wheere/api-gateway';
// Aliased so we don't shadow the global JS Map constructor used by `subs`.
import { Map as MaplibreMap } from 'maplibre-gl';
import { SITE_CONFIG_WEBSOCKET } from '../data/site-config-websocket.token';
import { SiteModelClient } from '../data/site-model.client';
import { SiteBuildingState } from '../site-model/site-building.state';
import { MapRoomLabelComponent } from '../site-model/map-room-label.component';
import { waitForWebsocket } from '../util/wait-for-websocket';
import {
  GatewayStateReading,
  GatewayStateTone,
  readAutoSync,
  readGpsdo,
  readSignalState,
} from '../data/gateway-live-state';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { TranslocoModule } from '@jsverse/transloco';

/** Dotted-line overlay for the configuration preview (#18 + polish): each multi-antenna
 *  gateway is drawn as a draggable marker at the gateway's own coordinates (or the antenna
 *  centroid when unset), with dashed lines to every antenna and an "RF OUT N" label at each
 *  midpoint. The integrator can drag the gateway marker to relocate the physical chassis
 *  without touching the antenna positions. */
export interface GatewayAntennaLink {
  gatewayId: string;
  gatewayName: string;
  /**
   * Current gateway origin lat/lng — either the gateway's own coords or, if unset, the
   * centroid of its antennas (computed by the parent so this component stays presentational).
   */
  originLng: number;
  originLat: number;
  /**
   * When true the inline gateway marker (iso SVG box) is suppressed — only the dashed
   * line + RF OUT label render. Used for single-antenna gateways with implicit position
   * tracking: gateway = antenna 0, no point drawing a marker on top of the antenna one.
   */
  hideGatewayMarker?: boolean;
  antennas: Array<{
    orderGateway: number;
    lng: number;
    lat: number;
    /** Resolved RF OUT (rfOut ?? orderGateway) — drives the dashed-line midpoint label. */
    rfOut: number;
  }>;
}

/** A live position update streamed by the trackermanager on `/topic/tracker/{id}/position`. */
/**
 * Barometre d'un site (`kind = BAROMETER`, trackermanager `GET /trackers?kind=BAROMETER&sitesId=`) :
 * position fixe + derniere mesure persistee par grandeur (ADR-020). Le live STOMP `/baro` (une grandeur
 * par message) est fusionne ici mesure par mesure.
 */
export interface SiteBarometer extends BarometerMarkerData {
  id: string;
  lastBaro?: BarometerReadingView | null;
}

interface LiveTrackerPosition {
  trackerId: string;
  name: string;
  color: string;
  latitude: number;
  longitude: number;
  altitude?: number;
  timestamp?: number;
  /** Heure de la position côté serveur (ms epoch), pour le personnage 3D. */
  measuredAt?: number;
}

const TRACKER_PALETTE = chartCustomColors;

@Component({
  selector: 'lib-site-view-map',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MapComponent,
    ControlComponent,
    GeoJSONSourceComponent,
    LayerComponent,
    IntegratorMultiSelectComponent,
    MapGatewayMarkerComponent,
    MapAntennaMarkerComponent,
    MapBarometerMarkerComponent,
    MapTrackerMobileMarkerComponent,
    MarkerComponent,
    MapRoomLabelComponent,
    CopyableTextComponent,
    FastSvgComponent,
    TranslocoModule,
  ],
  template: `
    <div
      [class]="
        fullscreen()
          ? 'fixed inset-0 z-50 flex h-screen w-screen flex-col gap-0 bg-white dark:bg-slate-900'
          : compact()
            ? 'flex h-full w-full flex-col'
            : 'flex h-full min-h-[480px] w-full flex-col gap-4 lg:flex-row'
      ">
      <div
        class="relative overflow-hidden bg-gray-100 dark:bg-slate-900"
        [class]="
          fullscreen()
            ? 'h-full w-full rounded-none border-0'
            : compact()
              ? 'h-full w-full rounded-none border-0'
              : 'h-[40vh] w-full rounded-xl border border-gray-200 lg:h-full dark:border-slate-700 ' +
                (panelOpen() ? 'lg:w-2/3' : 'lg:w-full')
        ">
        <button
          type="button"
          class="absolute left-3 top-3 z-40 inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white/95 px-2.5 text-xs font-bold uppercase tracking-wider text-gray-700 shadow-sm transition-colors hover:bg-white dark:border-slate-600 dark:bg-slate-800/95 dark:text-slate-200 dark:hover:bg-slate-800"
          [title]="
            fullscreen()
              ? ('siteViewMap.fullscreenTitleOn' | transloco)
              : ('siteViewMap.fullscreenTitleOff' | transloco)
          "
          (click)="fullscreen.set(!fullscreen())">
          <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
            @if (fullscreen()) {
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
            } @else {
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            }
          </svg>
          {{ fullscreen() ? ('siteViewMap.exit' | transloco) : ('siteViewMap.fullscreen' | transloco) }}
        </button>

        <!-- Bâtiment 3D du site (ADR-017) : étage affiché (coupe au-dessus) et vue 2D / 3D. -->
        @if (building.model(); as model) {
          <div
            class="absolute left-3 top-14 z-40 flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-white/95 p-1 shadow-sm dark:border-slate-600 dark:bg-slate-800/95">
            <span
              class="pl-1.5 pr-0.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              {{ 'siteViewMap.building.floor' | transloco }}
            </span>
            <div class="inline-flex gap-0.5" role="group" [attr.aria-label]="'siteViewMap.building.floor' | transloco">
              @for (l of model.levels; track l.id) {
                <button
                  type="button"
                  class="h-[26px] rounded px-2.5 text-xs font-bold"
                  [class]="
                    building.floor() === l.id
                      ? 'bg-gray-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700'
                  "
                  [attr.aria-pressed]="building.floor() === l.id"
                  (click)="building.setFloor(l.id)">
                  {{ l.name }}
                </button>
              }
              <button
                type="button"
                class="h-[26px] rounded px-2.5 text-xs font-bold"
                [class]="
                  building.floor() === 'all'
                    ? 'bg-gray-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700'
                "
                [attr.aria-pressed]="building.floor() === 'all'"
                (click)="building.setFloor('all')">
                {{ 'siteViewMap.building.roof' | transloco }}
              </button>
            </div>
            <span class="mx-0.5 h-5 w-px bg-gray-200 dark:bg-slate-600"></span>
            <div class="inline-flex gap-0.5" role="group">
              @for (v of [false, true]; track v) {
                <button
                  type="button"
                  class="h-[26px] rounded px-2.5 text-xs font-bold"
                  [class]="
                    building.is3d() === v
                      ? 'bg-gray-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700'
                  "
                  [attr.aria-pressed]="building.is3d() === v"
                  (click)="building.set3d(v)">
                  {{ v ? '3D' : '2D' }}
                </button>
              }
            </div>
          </div>
        }

        @if (mapStyle()) {
          <mgl-map
            [mapStyle]="mapStyle()"
            [zoom]="[15]"
            [fitBoundsOptions]="fitBoundsOptions()"
            [maxZoom]="building.model() ? 21 : 20"
            [movingMethod]="'jumpTo'"
            (mapLoad)="onMapLoad($event)"
            class="h-full w-full">
            @if (!compact()) {
              <mgl-control position="top-right">
                <!-- Wrapper card so the dropdown sits on a card matching the
                   fullscreen toggle on the left — single visual language for
                   every overlay control. -->
                <div class="flex items-start gap-2">
                  @if (!panelOpen() && !fullscreen()) {
                    <button
                      type="button"
                      class="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-200 bg-white/95 px-2.5 text-xs font-bold uppercase tracking-wider text-gray-700 shadow-sm hover:bg-white dark:border-slate-600 dark:bg-slate-800/95 dark:text-slate-200"
                      (click)="panelOpen.set(true)">
                      {{ 'siteViewMap.panel.open' | transloco }}
                    </button>
                  }
                  <div
                    class="rounded-md border border-gray-200 bg-white/95 p-1 shadow-sm dark:border-slate-600 dark:bg-slate-800/95">
                    <lib-integrator-multi-select
                      width="14rem"
                      size="sm"
                      accent="primary"
                      itemNoun="trackers"
                      [placeholder]="'siteViewMap.showMobileTrackers' | transloco"
                      [filterable]="true"
                      [options]="mobileTrackerOptions()"
                      [ngModel]="selectedMobileIds()"
                      (ngModelChange)="onMobileSelectionChange($event)" />
                  </div>
                </div>
              </mgl-control>
            }

            <!-- Barometers (kind = BAROMETER, flux 9) — teal gauge at their fixed position, live pressure in the popup. -->
            @for (b of barometers(); track b.id) {
              <lib-map-barometer-marker
                [barometer]="b"
                [reading]="baroReading(b)"
                [waitingLabel]="'siteViewMap.baroWaiting' | transloco"
                [offset]="building.offset(b.fixedLatitude, b.fixedLongitude, b.fixedAltitude)"
                [dimmed]="building.isOtherFloor(b.fixedLatitude, b.fixedLongitude, b.fixedAltitude)"
                [floorTag]="otherFloorTag(b.fixedLatitude, b.fixedLongitude, b.fixedAltitude)" />
            }

            <!-- Antennas (every gateway's reception points) — same palette cycle so each antenna
                 is visually distinct and the operator can tie a dot to a row by colour. Shown
                 whenever antennas are provided (the unified model has antennas on every gateway). -->
            @for (a of antennas(); track a.id; let i = $index) {
              <lib-map-antenna-marker
                [antenna]="toGwAntenna(a)"
                [color]="paletteColor(i)"
                [gatewayName]="antennaGatewayNames()[i]"
                [show]="false"
                [offset]="building.offset(a.latitude, a.longitude, a.altitude)" />
            }

            <!-- Live mobile-tracker dots — pulsing coloured circles (palette rotates). -->
            @for (p of livePositions(); track p.trackerId) {
              <lib-map-tracker-mobile-marker
                [trackerPosition]="{
                  id: p.trackerId,
                  name: p.name,
                  longitude: p.longitude,
                  latitude: p.latitude,
                  altitude: p.altitude,
                  color: p.color,
                }"
                [offset]="building.offset(p.latitude, p.longitude, headAltitude(p))"
                [dimmed]="building.isOtherFloor(p.latitude, p.longitude, p.altitude)"
                [floorTag]="otherFloorTag(p.latitude, p.longitude, p.altitude)" />
            }

            <!-- Noms des pièces de l'étage affiché, sur son plancher (bâtiment 3D). -->
            @for (r of building.roomLabels(); track r.id) {
              @if (building.zoom() >= 18.6) {
                <lib-map-room-label
                  [name]="r.name"
                  [lat]="r.lat"
                  [lon]="r.lon"
                  [areaM2]="r.areaM2"
                  [showArea]="building.zoom() >= 19.4"
                  [offset]="building.offset(r.lat, r.lon, r.alt)" />
              }
            }

            @for (link of gatewayAntennaLinks(); track link.gatewayId) {
              @for (ant of link.antennas; track ant.orderGateway) {
                <mgl-geojson-source
                  [id]="'gw-ant-line-' + link.gatewayId + '-' + ant.orderGateway"
                  [data]="buildLineFeature(link.originLng, link.originLat, ant.lng, ant.lat)" />
                <mgl-layer
                  [id]="'gw-ant-line-layer-' + link.gatewayId + '-' + ant.orderGateway"
                  [source]="'gw-ant-line-' + link.gatewayId + '-' + ant.orderGateway"
                  type="line"
                  [layout]="{ visibility: building.is3d() ? 'none' : 'visible' }"
                  [paint]="{ 'line-color': '#6B7280', 'line-width': 1.5, 'line-dasharray': [4, 3] }" />
                <mgl-geojson-source
                  [id]="'gw-ant-label-' + link.gatewayId + '-' + ant.orderGateway"
                  [data]="buildMidpointFeature(link.originLng, link.originLat, ant.lng, ant.lat, ant.rfOut)" />
                <mgl-layer
                  [id]="'gw-ant-label-layer-' + link.gatewayId + '-' + ant.orderGateway"
                  [source]="'gw-ant-label-' + link.gatewayId + '-' + ant.orderGateway"
                  type="symbol"
                  [layout]="{
                    visibility: building.is3d() ? 'none' : 'visible',
                    'text-field': ['get', 'label'],
                    'text-size': 10,
                    'text-anchor': 'center',
                    'text-offset': [0, -0.8],
                  }"
                  [paint]="{ 'text-color': '#374151', 'text-halo-color': '#ffffff', 'text-halo-width': 1 }" />
              }
              <!-- Gateway pin — isometric SVG of the actual hardware (4 RF OUT ports
                   visible) sits on top of the dashed lines. Suppressed when
                   {@code hideGatewayMarker=true} (single-antenna gateway with implicit
                   position tracking: gateway = antenna 0, no point doubling the marker).
                   Draggable in the configuration preview ({@code draggableGateways=true}).
                   The 14×8 frame matches the SVG's 430×235 aspect. -->
              @if (!link.hideGatewayMarker) {
                <mgl-marker
                  [lngLat]="[link.originLng, link.originLat]"
                  [draggable]="draggableGateways()"
                  (markerDragEnd)="onGatewayMarkerDragEnd($event, link.gatewayId)">
                  <!-- Title attribute carries the gateway name for hover — no visible pill
                     under the SVG (kept clutter-free; the antenna popups already name the
                     owning gateway). -->
                  <div
                    class="flex items-center justify-center"
                    [class.cursor-grab]="draggableGateways()"
                    [class.active:cursor-grabbing]="draggableGateways()"
                    [title]="link.gatewayName">
                    <!-- text-black with no dark variant: the gateway iso strokes use
                       currentColor, and the chassis must stay black on every basemap
                       (dark mode included). Antennas already carry their own palette
                       colour via the antenna marker. -->
                    <div class="flex h-8 w-14 items-center justify-center text-black">
                      <fast-svg name="gateway-iso" size="100%" />
                    </div>
                  </div>
                </mgl-marker>
              }
            }
          </mgl-map>
        } @else {
          <div class="flex h-full items-center justify-center text-sm text-gray-500">
            {{ 'siteViewMap.noTiles' | transloco }}
          </div>
        }
      </div>
      <div
        class="overflow-auto rounded-xl border border-gray-200 bg-white p-4 lg:h-full lg:w-1/3 dark:border-slate-700 dark:bg-slate-800"
        [class.hidden]="fullscreen() || compact() || !panelOpen()">
        @if (building3d()) {
          <div class="mb-3 flex items-center justify-between gap-2">
            <span class="text-sm font-bold text-gray-900 dark:text-slate-100">{{
              'siteViewMap.panel.title' | transloco
            }}</span>
            <button
              type="button"
              class="grid size-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-700"
              [attr.aria-label]="'siteViewMap.panel.close' | transloco"
              [title]="'siteViewMap.panel.close' | transloco"
              (click)="panelOpen.set(false)">
              <svg
                class="size-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        }

        <!-- Bâtiment 3D du site (ADR-017) -->
        @if (building.model(); as model) {
          <div class="mb-4">
            <button
              type="button"
              class="mb-2 flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
              [attr.aria-expanded]="!folded()['building']"
              (click)="toggleFold('building')">
              <svg
                class="size-3.5 shrink-0 transition-transform"
                [class.rotate-90]="!folded()['building']"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true">
                <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ 'siteViewMap.building.title' | transloco }}
            </button>
            @if (!folded()['building']) {
              <div
                class="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40">
                <span class="min-w-0 truncate text-sm font-bold text-gray-900 dark:text-slate-100">{{
                  model.fileName
                }}</span>
                <span class="ml-auto shrink-0 text-xs text-gray-500 dark:text-slate-400">
                  {{
                    'siteViewMap.building.summary'
                      | transloco: { levels: model.levels.length, rooms: model.rooms.length }
                  }}
                </span>
              </div>
            }
          </div>
        }

        @if (sidebarGateways().length === 0 && antennas().length === 0 && barometers().length === 0) {
          <div class="flex h-full items-center justify-center text-center text-sm text-gray-500 dark:text-slate-400">
            {{ 'siteViewMap.empty' | transloco }}
          </div>
        }

        <!-- Barometers -->
        @if (barometers().length > 0) {
          <div class="mb-4">
            <button
              type="button"
              class="mb-2 flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
              [attr.aria-expanded]="!folded()['barometers']"
              (click)="toggleFold('barometers')">
              <svg
                class="size-3.5 shrink-0 transition-transform"
                [class.rotate-90]="!folded()['barometers']"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true">
                <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ 'siteViewMap.barometersWithCount' | transloco: { count: barometers().length } }}
            </button>
            @if (!folded()['barometers']) {
              <div class="flex flex-col gap-2">
                @for (b of barometers(); track b.id) {
                  @let r = baroReading(b);
                  <div
                    class="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
                    <div class="flex items-center justify-between gap-2">
                      <div class="flex min-w-0 items-center gap-2">
                        <span class="size-5 shrink-0 text-teal-500">
                          <fast-svg name="barometer" size="100%" />
                        </span>
                        <span class="truncate text-sm font-bold text-gray-900 dark:text-slate-100">{{
                          b.name || b.deviceId
                        }}</span>
                      </div>
                      <span [class]="statusChipClass(b.status ?? undefined)">
                        <span class="h-1.5 w-1.5 rounded-full" [class]="statusDotClass(b.status ?? undefined)"></span>
                        {{ 'status.' + (b.status ?? 'UNKNOWN') | transloco }}
                      </span>
                    </div>
                    <div
                      class="mt-2 flex items-baseline justify-between gap-2 text-xs text-gray-600 dark:text-slate-400">
                      @if (r) {
                        <span class="text-lg font-bold tabular-nums text-gray-900 dark:text-slate-100">
                          @if (r.pressure; as p) {
                            {{ p.value.toFixed(1) }} <span class="text-xs font-normal">hPa</span>
                          } @else {
                            — <span class="text-xs font-normal">hPa</span>
                          }
                        </span>
                        <span>
                          @if (r.temperature; as t) {
                            {{ t.value.toFixed(1) }} °C
                          }
                          @if (r.temperature && r.pressure) {
                            ·
                          }
                          @if (r.pressure; as p) {
                            {{ p.timestamp | date: 'HH:mm:ss' }}
                          }
                        </span>
                      } @else {
                        <span class="italic">{{ 'siteViewMap.baroWaiting' | transloco }}</span>
                      }
                    </div>
                    @if (!hasFixedPosition(b)) {
                      <div class="mt-1 text-[11px] text-amber-600">{{ 'siteViewMap.baroNoPosition' | transloco }}</div>
                    }
                    @if (building.roomAt(b.fixedLatitude, b.fixedLongitude, b.fixedAltitude); as room) {
                      <div class="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
                        {{ building.levelAt(b.fixedLatitude, b.fixedLongitude, b.fixedAltitude)?.name }} ·
                        {{ room.name }}
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }

        @if (sidebarGateways().length > 0) {
          <div class="mb-4">
            <div class="mb-2 flex items-center justify-between gap-2">
              <button
                type="button"
                class="flex items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
                [attr.aria-expanded]="!folded()['gateways']"
                (click)="toggleFold('gateways')">
                <svg
                  class="size-3.5 shrink-0 transition-transform"
                  [class.rotate-90]="!folded()['gateways']"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                {{ 'siteViewMap.gatewaysWithCount' | transloco: { count: sidebarGateways().length } }}
              </button>
              <div class="flex items-center gap-2.5">
                <!-- Mise à jour en direct des états (absente quand l'hôte ne la fournit pas). -->
                @if (liveConnected() === true) {
                  <span
                    class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-slate-400"
                    [title]="'siteViewMap.live.onTitle' | transloco">
                    <span
                      class="bg-aquamarine-500 size-1.5 animate-pulse rounded-full motion-reduce:animate-none"></span>
                    {{ 'siteViewMap.live.on' | transloco }}
                  </span>
                } @else if (liveConnected() === false) {
                  <span
                    class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-red-700 dark:text-red-300">
                    <span class="size-1.5 rounded-full bg-red-500"></span>
                    {{ 'siteViewMap.live.off' | transloco }}
                  </span>
                }
                <!-- List / card view switch for the gateway sidebar. -->
                <div class="flex items-center gap-0.5 rounded-md border border-gray-200 p-0.5 dark:border-slate-600">
                  <button
                    type="button"
                    class="flex size-6 items-center justify-center rounded"
                    [class]="
                      !gwListView()
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700'
                    "
                    [title]="'siteViewMap.viewMode.card' | transloco"
                    (click)="gwListView.set(false)">
                    <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M4 5h7v7H4zM13 5h7v7h-7zM4 14h7v5H4zM13 14h7v5h-7z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="flex size-6 items-center justify-center rounded"
                    [class]="
                      gwListView()
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700'
                    "
                    [title]="'siteViewMap.viewMode.list' | transloco"
                    (click)="gwListView.set(true)">
                    <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            @if (!folded()['gateways']) {
              @if (liveConnected() === false) {
                <div
                  class="mb-2.5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
                  <svg
                    class="mt-0.5 size-3.5 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true">
                    <path
                      d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
                  </svg>
                  <span>{{ 'siteViewMap.live.offBanner' | transloco }}</span>
                </div>
              }
              <!-- Résumé du site : en ligne, signal en l'air, auto-synchro verrouillée. -->
              @let sum = gatewaySummary();
              <div
                class="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-slate-900/40 dark:text-slate-400">
                @for (part of sum.parts; track part.key) {
                  <span class="inline-flex items-center gap-1.5">
                    <span class="size-1.5 rounded-full" [class]="summaryDotClass(part.count, sum.total)"></span>
                    <b class="tabular-nums text-gray-900 dark:text-slate-100">{{ part.count }}/{{ sum.total }}</b>
                    {{ 'siteViewMap.summary.' + part.key | transloco }}
                  </span>
                }
              </div>
              <!-- Vue cartes : deux colonnes seulement si le panneau a la place (sinon le nom de la
                   gateway disparaissait derrière la pastille de statut). -->
              <div
                [class]="
                  gwListView()
                    ? 'flex flex-col gap-2.5'
                    : 'grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] items-start gap-2.5'
                ">
                @for (g of sidebarGateways(); track g.id; let gi = $index) {
                  <div
                    class="flex flex-col gap-2.5 rounded-xl border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/60">
                    <!-- Header: colored gateway icon tile + name/order stacked + status pill. Giving the
                       name its own line (order underneath) keeps it readable in the 2-up card grid. -->
                    <div class="flex items-start gap-2">
                      <span
                        class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-slate-700"
                        [style.color]="paletteColor(gi)">
                        <fast-svg name="gateway" size="55%" />
                      </span>
                      <div class="min-w-0 flex-1">
                        <div class="truncate text-sm font-bold leading-tight text-gray-900 dark:text-slate-100">
                          {{ g.name }}
                        </div>
                        <div class="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                          {{ 'siteViewMap.labels.order' | transloco }} {{ g.orderSite ?? '—' }}
                        </div>
                      </div>
                      <span [class]="statusChipClass(g.status)">
                        <span class="h-1.5 w-1.5 rounded-full" [class]="statusDotClass(g.status)"></span>
                        {{ 'status.' + (g.status ?? 'UNKNOWN') | transloco }}
                      </span>
                    </div>

                    <!-- États remontés par la gateway (signal_state, pg), lus comme le firmware les publie
                       (gateway-live-state.ts). Hors ligne : dernier état connu, jamais en vert. -->
                    @let st = gatewayStates().get(g.id ?? '');
                    @if (st && st.rows.length) {
                      <div class="flex flex-col gap-1.5" [class.opacity-80]="st.stale">
                        @for (row of st.rows; track row.label) {
                          <div class="flex flex-wrap items-baseline justify-between gap-x-2">
                            <span
                              class="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400"
                              >{{ 'siteViewMap.chips.' + row.label | transloco }}</span
                            >
                            @if (row.reading; as r) {
                              <span
                                class="inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold"
                                [class]="toneTextClass(st.stale ? 'stale' : r.tone)"
                                [title]="'siteViewMap.state.rawTitle' | transloco: { raw: r.raw }">
                                <span
                                  class="size-2 shrink-0 rounded-full"
                                  [class]="toneDotClass(st.stale ? 'stale' : r.tone)"></span>
                                {{ r.key ? ('siteViewMap.state.' + r.key | transloco) : r.raw }}
                              </span>
                            } @else {
                              <span class="text-xs text-gray-400 dark:text-slate-500">{{
                                'siteViewMap.state.notReported' | transloco
                              }}</span>
                            }
                          </div>
                        }
                      </div>
                    } @else {
                      <div
                        class="flex items-start gap-1.5 rounded-lg border border-dashed border-gray-300 px-2 py-1.5 text-xs text-gray-600 dark:border-slate-600 dark:text-slate-400">
                        {{ 'siteViewMap.state.none' | transloco }}
                      </div>
                    }
                    @if (receivedText(g); as foot) {
                      <div
                        class="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-[11px] text-gray-500 dark:text-slate-400">
                        <span class="whitespace-nowrap" [class.italic]="st?.stale">{{
                          foot.key | transloco: { age: foot.age }
                        }}</span>
                        @if (showRawState() && (g.config?.pg || g.config?.stateSignal)) {
                          <button
                            type="button"
                            class="inline-flex shrink-0 items-center gap-1 rounded px-1 font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                            [attr.aria-expanded]="!!rawOpen()[g.id ?? '']"
                            (click)="toggleRaw(g.id)">
                            <svg
                              class="size-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              aria-hidden="true">
                              <path d="M13.5 6L10 18.5M6.5 8.5L3 12l3.5 3.5M17.5 8.5L21 12l-3.5 3.5" />
                            </svg>
                            {{ 'siteViewMap.state.raw' | transloco }}
                          </button>
                        }
                      </div>
                    }
                    @if (showRawState() && rawOpen()[g.id ?? '']) {
                      <div class="flex flex-col gap-1.5 text-[11px]">
                        @for (block of rawBlocks(g); track block.topic) {
                          <div class="min-w-0">
                            <div class="mb-0.5 font-semibold text-gray-500 dark:text-slate-400">{{ block.topic }}</div>
                            <pre
                              class="overflow-x-auto whitespace-pre rounded bg-gray-900 p-2 text-[10.5px] leading-snug text-gray-100 dark:bg-black/40"
                              >{{ block.json }}</pre
                            >
                          </div>
                        }
                      </div>
                    }

                    <!-- Copyable lat,lng,alt tuple (crops with … on a narrow card; hover shows the full
                       value, click copies). -->
                    <lib-copyable-text
                      class="min-w-0 text-xs tabular-nums text-gray-600 dark:text-slate-400"
                      [text]="positionText(g.latitude, g.longitude, g.altitude, 5)" />

                    @if (g.antennas?.length) {
                      <div class="flex flex-col gap-1.5 border-t border-gray-200 pt-2.5 dark:border-slate-700">
                        @for (a of g.antennas; track a.id; let ai = $index) {
                          <div class="flex items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5 dark:bg-slate-900/40">
                            <span
                              class="flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-bold text-white"
                              [style.background]="paletteColor(globalAntennaIndex(gi, ai))">
                              A{{ a.orderGateway ?? ai }}
                            </span>
                            <div class="min-w-0 flex-1">
                              <div class="truncate text-xs font-semibold text-gray-900 dark:text-slate-100">
                                {{ 'siteViewMap.antennaLabel' | transloco: { i: a.orderGateway ?? ai } }}
                              </div>
                              <lib-copyable-text
                                class="min-w-0 text-xs tabular-nums text-gray-500 dark:text-slate-400"
                                [text]="positionText(a.latitude, a.longitude, a.altitude, 5)" />
                            </div>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }

        <!-- Live mobile trackers selected from the map control -->
        @if (livePositions().length > 0) {
          <div>
            <button
              type="button"
              class="mb-2 flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
              [attr.aria-expanded]="!folded()['live']"
              (click)="toggleFold('live')">
              <svg
                class="size-3.5 shrink-0 transition-transform"
                [class.rotate-90]="!folded()['live']"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true">
                <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ 'siteViewMap.liveTrackers' | transloco }}
            </button>
            @if (!folded()['live']) {
              <div class="flex flex-col gap-2">
                @for (p of livePositions(); track p.trackerId) {
                  <div
                    class="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-900/40">
                    <span class="size-2.5 shrink-0 rounded-full" [style.background]="p.color"></span>
                    <span class="font-mono text-gray-900 dark:text-slate-100">{{ p.name }}</span>
                    @if (building.levelAt(p.latitude, p.longitude, p.altitude); as level) {
                      <span class="text-gray-500 dark:text-slate-400">
                        {{ level.name }}
                        @if (building.roomAt(p.latitude, p.longitude, p.altitude); as room) {
                          · {{ room.name }}
                        }
                      </span>
                    }
                    <span class="ml-auto text-gray-500 dark:text-slate-400"
                      >{{ p.latitude.toFixed(5) }}, {{ p.longitude.toFixed(5) }}</span
                    >
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>
  `,
})
export class SiteViewMapComponent implements OnDestroy {
  gateways = input<Gateway[]>([]);
  antennas = input<ApiSiteAntenna[]>([]);
  /** Parallel to {@link antennas} — name of the owning gateway for each antenna at the
   *  same index. Passed through to the marker popup (#16) and to the sidebar labels.
   *  Optional: when empty, popups fall back to the bare "RF OUT N" label. */
  antennaGatewayNames = input<(string | undefined)[]>([]);
  /** Full gateway objects (including their embedded `antennas[]`) used to render the
   *  hierarchical sidebar where each gateway card lists its own antennas underneath
   *  (#15). Distinct from {@link gateways} (which the map markers consume) so the
   *  sidebar can stay grouped even if the map's gateway list is filtered later. */
  gatewaysFull = input<Gateway[]>([]);

  /** Mise à jour en direct des états des gateways : true = active, false = coupée, null = l'hôte
   *  ne la fournit pas (pas d'indicateur). */
  liveConnected = input<boolean | null>(null);
  /** Heure (ms) du dernier message reçu en direct, par id de gateway. */
  gatewayLastMessageAt = input<Record<string, number>>({});
  /** Affiche « État brut » (JSON reçu de la gateway) sous chaque carte. */
  showRawState = input(false);

  /** #18 + polish — dashed lines from each multi-antenna gateway's marker to every antenna,
   *  with the RF OUT label at the midpoint. Empty by default; populated by the configuration
   *  preview only. A gateway marker is rendered for every entry (draggable) so the integrator
   *  can place the chassis on the map independently of the antennas. */
  gatewayAntennaLinks = input<GatewayAntennaLink[]>([]);

  /** Emitted when the integrator drags a gateway marker on the preview map. The parent
   *  uses this to update positionDraft[i].latitude/longitude; the dashed lines then re-render
   *  through the signal-driven {@link gatewayAntennaLinks} input. */
  readonly gatewayDragged = output<{ gatewayId: string; lng: number; lat: number }>();

  /** Internal fullscreen state — toggled by the overlay button on the map and by Esc. */
  protected readonly fullscreen = signal(false);

  /** Gateway sidebar layout — `true` = detailed rows stacked in one column (list, default),
   *  `false` = compact tiles two-across (card grid). Toggled by the list/card switch. */
  protected readonly gwListView = signal(true);

  /** Esc exits fullscreen (only when fullscreen is on, so the key still does whatever
   * else the page wants the rest of the time). The handler is attached to the document so
   * it works even when the focus is on the map canvas. */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.fullscreen()) this.fullscreen.set(false);
  }

  /** ApiSiteAntenna and ApiGwAntenna have identical shape (id, lat, lon, alt, orderGateway,
   * gatewayId) — the cast just satisfies TypeScript's nominal typing for the shared-ui
   * marker which expects the @wheere/api-gateway flavour. */
  protected toGwAntenna(a: ApiSiteAntenna): ApiGwAntenna {
    return a as unknown as ApiGwAntenna;
  }
  /** All trackers attached to the site — exposed in the multiselect for live tracking. */
  trackers = input<Tracker[]>([]);
  /** Barometers of the site (kind = BAROMETER) — markers at their fixed position, live pressure via STOMP `/baro`. */
  barometers = input<SiteBarometer[]>([]);
  /** Live readings keyed by barometer id (STOMP `/topic/tracker/{id}/baro`), merged per quantity on top of `lastBaro`. */
  protected readonly liveBaro = signal<Record<string, BarometerReadingView>>({});

  /**
   * Whether the gateway markers rendered from {@link gatewayAntennaLinks} accept drag.
   * The configuration preview wires this to {@code true} so the integrator can place
   * the chassis on the map; the read-only site view tab keeps the default {@code false}
   * so the overlay is purely informational. */
  draggableGateways = input<boolean>(true);
  /** MapTiler key supplied by the host app — empty string falls back to the OSM raster style. */
  maptilesKey = input<string>('');

  compact = input<boolean>(false);

  /** Site affiché : sert à charger son modèle 3D quand {@link building3d} est vrai. */
  siteId = input<string | null>(null);
  /**
   * Bâtiment 3D du site sur la carte (ADR-017, inside seulement) : chargé s'il existe, avec le
   * choix d'étage et la vue 2D / 3D ; marqueurs placés à leur altitude.
   */
  building3d = input<boolean>(false);
  /** Mannequin des personnages 3D des trackers suivis (GLB animé repos / marche), ou `null`. */
  personModelUrl = input<string | null>(null);

  /** Bâtiment 3D : modèle, étage, 2D / 3D, décalages d'altitude des marqueurs. */
  protected readonly building = new SiteBuildingState();
  readonly #siteModelClient = inject(SiteModelClient);
  #loadedModelFor: string | null = null;

  /** Panneau de droite ouvert ; fermé, la carte prend toute la largeur (bouton « Infos » pour le rouvrir). */
  protected readonly panelOpen = signal(true);
  /**
   * Sections repliées du panneau. Avec le bâtiment 3D (inside), Gateways est repliée par défaut ;
   * ailleurs (integrator) tout reste ouvert, comme avant.
   */
  protected readonly folded = linkedSignal<Record<string, boolean>>(
    (): Record<string, boolean> => (this.building3d() ? { gateways: true } : {})
  );

  protected toggleFold(key: string): void {
    this.folded.update(f => ({ ...f, [key]: !f[key] }));
  }

  /** Étage du marqueur quand il n'est pas celui affiché (étiquette sous le marqueur estompé). */
  protected otherFloorTag(
    lat: number | null | undefined,
    lon: number | null | undefined,
    alt: number | null | undefined
  ): string | null {
    return this.building.isOtherFloor(lat, lon, alt) ? (this.building.levelAt(lat, lon, alt)?.name ?? null) : null;
  }

  /** Pastille d'un tracker suivi : au-dessus de la tête de son personnage (plancher + 2,05 m) quand il y a un bâtiment. */
  protected headAltitude(p: LiveTrackerPosition): number | undefined {
    if (p.altitude == null) {
      return undefined;
    }
    const aboveFloor = this.floorOffset(p);
    return aboveFloor == null ? p.altitude : p.altitude - aboveFloor + 2.05;
  }

  /** Hauteur de la position au-dessus du plancher de son étage (repère du plan), ou `null`. */
  private floorOffset(p: LiveTrackerPosition): number | null {
    const model = this.building.model();
    if (!model || p.altitude == null) {
      return null;
    }
    const level = this.building.levelAt(p.latitude, p.longitude, p.altitude);
    const z = geoToModel(model.georef, { lat: p.latitude, lon: p.longitude, alt: p.altitude }).z;
    return level ? z - level.z : null;
  }

  protected readonly selectedMobileIds = signal<string[]>([]);
  protected readonly livePositions = signal<LiveTrackerPosition[]>([]);

  /** Trackers selectable in the multiselect — every site tracker that isn't a barometer. */
  protected readonly mobileTrackers = computed(() => {
    const baroIds = new Set(this.barometers().map(b => b.id));
    return this.trackers()
      .filter(t => t.id && !baroIds.has(t.id))
      .map((t, i) => ({ id: t.id!, name: t.name || t.deviceId || `Tracker ${i}` }));
  });

  /**
   * Same data as {@link mobileTrackers}, reshaped for {@link IntegratorMultiSelectComponent}'s
   * {label, value} contract. Kept as a separate computed (not a one-shot map in
   * the template) so OnPush can reuse the array reference between renders.
   */
  protected readonly mobileTrackerOptions = computed(() =>
    this.mobileTrackers().map(t => ({ label: t.name, value: t.id }))
  );

  protected readonly mapStyle = computed(() => {
    const key = this.maptilesKey();
    if (key) {
      return `https://api.maptiler.com/maps/streets-v2/style.json?key=${key}`;
    }
    return 'https://demotiles.maplibre.org/style.json';
  });

  /**
   * Mirrors the inside MapPreviewComponent pattern: we don't bind {@code [center]} at all
   * (which previously routed every position edit through ngx-maplibre-gl's `move()` and
   * fired a `flyTo` even with `movingMethod=jumpTo` set via static attribute).
   *
   * <p>Instead the map's initial framing comes from a single imperative
   * {@code map.fitBounds()} call inside {@link onMapLoad}, plus a one-shot effect that
   * fires once the inputs resolve. Both paths consume {@link fitBoundsOptions}
   * (animate: false). After {@link boundsFitted} flips true, edits to antennas /
   * gateways / tracker positions never move the camera again.
   */
  private readonly map = signal<MaplibreMap | undefined>(undefined);
  private boundsFitted = false;

  /**
   * Map fit-bounds payload: prefer antenna positions (the reception-point cluster the
   * integrator is actually placing), falling back to gateways then the barometers.
   * Returns `undefined` when no positioned entity exists — the map then keeps its
   * default zoom until inputs arrive.
   */
  protected readonly fitBounds = computed<[[number, number], [number, number]] | undefined>(() => {
    const coords: { lng: number; lat: number }[] = [];
    for (const a of this.antennas()) {
      if (typeof a.longitude === 'number' && typeof a.latitude === 'number') {
        coords.push({ lng: a.longitude, lat: a.latitude });
      }
    }
    if (coords.length === 0) {
      for (const g of this.gateways()) {
        if (typeof g.longitude === 'number' && typeof g.latitude === 'number') {
          coords.push({ lng: g.longitude, lat: g.latitude });
        }
      }
      for (const b of this.barometers()) {
        if (typeof b.fixedLongitude === 'number' && typeof b.fixedLatitude === 'number') {
          coords.push({ lng: b.fixedLongitude, lat: b.fixedLatitude });
        }
      }
    }
    return coords.length > 0 ? getFitBounds(coords) : undefined;
  });

  /** animate:false everywhere so the one-shot fit is instant — no flying. */
  protected readonly fitBoundsOptions = computed(() => ({
    padding: this.fitBounds() ? calculateDynamicPadding(this.fitBounds()!) : 20,
    animate: false,
  }));

  #ws = inject(SITE_CONFIG_WEBSOCKET, { optional: true });

  /** Active subscriptions keyed by tracker id so we can unsubscribe selectively on toggle. */
  private subs = new Map<string, { unsubscribe(): void }>();
  /** Live `/baro` subscriptions keyed by barometer id — follow the {@link barometers} input. */
  private baroSubs = new Map<string, { unsubscribe(): void }>();
  /** Flipped in ngOnDestroy so pending {@link waitForWebsocket} polls stop. */
  private destroyed = false;

  private readonly clock = setInterval(() => {
    if (this.liveConnected() !== null || Object.keys(this.gatewayLastMessageAt()).length) {
      this.now.set(Date.now());
    }
  }, 1000);

  constructor() {
    // Barometers come and go with the site: subscribe to the live pressure of each one shown,
    // drop the subscriptions of those no longer listed.
    effect(() => {
      const wanted = new Set(this.barometers().map(b => b.id));
      for (const [id, sub] of this.baroSubs) {
        if (!wanted.has(id)) {
          sub.unsubscribe();
          this.baroSubs.delete(id);
        }
      }
      for (const id of wanted) {
        if (!this.baroSubs.has(id)) void this.subscribeToBaro(id);
      }
    });

    // Bâtiment 3D : chargé une fois par site, quand la carte est prête (ADR-017).
    effect(() => {
      const map = this.map();
      const siteId = this.siteId();
      if (!map || !siteId || !this.building3d() || this.#loadedModelFor === siteId) {
        return;
      }
      this.#loadedModelFor = siteId;
      void this.loadBuilding(map, siteId);
    });

    // Un personnage par tracker suivi en direct, sur le plancher de son étage.
    effect(() => {
      const people = this.livePositions()
        .filter(p => p.altitude != null)
        .map(p => ({
          id: p.trackerId,
          color: p.color,
          lat: p.latitude,
          lon: p.longitude,
          alt: p.altitude as number,
          measuredAt: p.measuredAt,
        }));
      if (this.building.model()) {
        this.building.setPeople(people);
      }
    });

    // One-shot auto-fit: triggers exactly once when both the map is mounted AND we have
    // real bounds to fit. After {@link boundsFitted} flips true the effect is a cheap
    // no-op — editing antennas / gateways / tracker positions never moves the camera
    // again. Same pattern as inside's MapPreviewComponent.
    effect(() => {
      const m = this.map();
      const b = this.fitBounds();
      if (m && b && !this.boundsFitted) {
        m.fitBounds(b, this.fitBoundsOptions());
        this.boundsFitted = true;
      }
    });
  }

  // ngAfterViewInit intentionally omitted — initial framing is handled by
  // {@link onMapLoad} + the constructor effect.

  /**
   * Map ready callback. Stores the instance for the auto-fit effect AND does an
   * immediate fitBounds if the inputs have already arrived (covers the case where the
   * map mounts AFTER the data — common when this component is wrapped in a `@defer`).
   */
  protected onMapLoad(mapInstance: MaplibreMap): void {
    this.map.set(mapInstance);
    const b = this.fitBounds();
    if (b && !this.boundsFitted) {
      mapInstance.fitBounds(b, this.fitBoundsOptions());
      this.boundsFitted = true;
    }
  }

  /** Modèle 3D du site : métadonnées puis GLB ; un site sans modèle garde sa carte 2D. */
  private async loadBuilding(map: MaplibreMap, siteId: string): Promise<void> {
    try {
      const model = await this.#siteModelClient.get(siteId);
      if (!model || this.destroyed) {
        return;
      }
      const glb = await this.#siteModelClient.content(siteId);
      if (this.destroyed) {
        return;
      }
      await this.building.attach(map, model, glb, this.personModelUrl());
    } catch (e) {
      // Visible : la carte reste en 2D, l'erreur est remontée en console et dans l'état.
      console.error('[site-view-map] modèle 3D du site', siteId, e);
      this.building.error.set(String(e));
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.clock);
    this.building.detach();
    this.destroyed = true;
    for (const s of this.subs.values()) s.unsubscribe();
    this.subs.clear();
    for (const s of this.baroSubs.values()) s.unsubscribe();
    this.baroSubs.clear();
  }

  /** Persisted `lastBaro` of the barometer with the live measures of this page merged in; null if nothing at all. */
  protected baroReading(b: SiteBarometer): BarometerReadingView | null {
    const r = this.liveBaro()[b.id] ?? b.lastBaro ?? null;
    return r && (r.pressure || r.temperature || r.battery) ? r : null;
  }

  protected hasFixedPosition(b: SiteBarometer): boolean {
    return typeof b.fixedLongitude === 'number' && typeof b.fixedLatitude === 'number';
  }

  /** Same connection dance as {@link subscribeToTracker}; the payload is the flux-9 contract. */
  private async subscribeToBaro(id: string): Promise<void> {
    const ws = this.#ws;
    if (!ws) {
      console.warn(`[site-view-map] SITE_CONFIG_WEBSOCKET not provided — no live pressure for barometer ${id}`);
      return;
    }
    try {
      await ws.initializeStomp();
    } catch {
      return;
    }
    const ready = await waitForWebsocket(
      () => ws.isWebSocketConnected(),
      () => this.destroyed || !this.barometers().some(b => b.id === id)
    );
    if (!ready || this.baroSubs.has(id)) return;
    try {
      const sub = ws.subscribe<unknown>(`/topic/tracker/${id}/baro`, msg => {
        const measure = parseBaroMeasure(msg);
        if (!measure) return;
        // First live measure starts from the persisted lastBaro so the other quantities stay visible.
        const current = this.liveBaro()[id] ?? this.barometers().find(b => b.id === id)?.lastBaro;
        this.liveBaro.set({ ...this.liveBaro(), [id]: mergeBaroMeasure(current, measure) });
      });
      this.baroSubs.set(id, sub);
    } catch (e) {
      console.warn('[site-view-map] subscribe failed for barometer %s', id, e);
    }
  }

  /** Cycle through the shared chartCustomColors so g0/a0=pink, g1/a1=purple, g2/a2=yellow,
   * g3/a3=cyan, … (same palette as the telemetry chart). The map markers and the right-hand
   * sidebar swatches both use this so the operator can match a dot to a row by colour. */
  protected paletteColor(i: number): string {
    return chartCustomColors[i % chartCustomColors.length];
  }

  /** Sidebar groups by gateway and lists each gateway's antennas underneath. Prefer
   *  {@link gatewaysFull} (carries the embedded `antennas[]`) — fall back to
   *  {@link gateways} so legacy callers that only wire the map-only input still
   *  render the gateway cards (just without nested antennas). */
  protected readonly sidebarGateways = computed<Gateway[]>(() =>
    this.gatewaysFull().length > 0 ? this.gatewaysFull() : this.gateways()
  );

  /** Map (gatewayIndex, antennaIndexWithinGateway) → flat antenna index. Used to look up
   *  the palette colour so the sidebar swatch and the map marker share the same colour
   *  for every antenna (palette is keyed by position in the flat {@link antennas} list). */
  protected globalAntennaIndex(gatewayIdx: number, antennaIdx: number): number {
    const gws = this.sidebarGateways();
    let acc = 0;
    for (let i = 0; i < gatewayIdx && i < gws.length; i++) {
      acc += gws[i].antennas?.length ?? 0;
    }
    return acc + antennaIdx;
  }

  /** #18 — build a FeatureCollection containing a single LineString between origin and
   *  target. Wrapped in a FC so the @maplibre/ngx-maplibre-gl source binding accepts it
   *  uniformly with the midpoint feature below. */
  protected buildLineFeature(
    originLng: number,
    originLat: number,
    targetLng: number,
    targetLat: number
  ): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [originLng, originLat],
              [targetLng, targetLat],
            ],
          },
          properties: {},
        },
      ],
    };
  }

  /**
   * Maplibre's marker drag-end event hands us a MarkerComponent reference; pull the
   * current lngLat off it and bubble up to the parent so positionDraft is the single
   * source of truth (the marker's position re-binds through the input next render).
   * The event payload's exact shape isn't typed by the wrapper so we duck-type it.
   */
  protected onGatewayMarkerDragEnd(evt: unknown, gatewayId: string): void {
    // ngx-maplibre-gl emits the MarkerComponent itself on (markerDragEnd); its
    // `.getLngLat()` returns the maplibre LngLat the user dropped on.
    const marker =
      (evt as { target?: { getLngLat?: () => { lng: number; lat: number } } })?.target ??
      (evt as { getLngLat?: () => { lng: number; lat: number } });
    const lngLat = marker?.getLngLat?.();
    if (!lngLat || !Number.isFinite(lngLat.lng) || !Number.isFinite(lngLat.lat)) return;
    this.gatewayDragged.emit({ gatewayId, lng: lngLat.lng, lat: lngLat.lat });
  }

  /** #18 — single Point feature at the midpoint of the link, carrying the "RF OUT{N}"
   *  label so the symbol layer's text-field expression can pull it via `['get', 'label']`. */
  protected buildMidpointFeature(
    originLng: number,
    originLat: number,
    targetLng: number,
    targetLat: number,
    order: number
  ): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [(originLng + targetLng) / 2, (originLat + targetLat) / 2] },
          properties: { label: `RF OUT${order}` },
        },
      ],
    };
  }

  protected fmt(n: number | null | undefined): string {
    if (n == null || isNaN(n)) return 'N/A';
    return n.toFixed(5);
  }

  /**
   * Plain-text lat/lng/alt tuple shaped exactly like the cell displays it. Used by
   * {@link CopyableTextComponent} so the clipboard payload matches the visible string —
   * a paste into Slack / SMS lands verbatim. `digits` controls the lat/lng precision;
   * altitude is fixed to 1 decimal because that's what the operator dictates over the
   * phone.
   */
  protected positionText(
    lat: number | null | undefined,
    lng: number | null | undefined,
    alt: number | null | undefined,
    digits = 5
  ): string {
    const fmtCoord = (n: number | null | undefined): string => (n == null || isNaN(n) ? 'N/A' : n.toFixed(digits));
    const fmtAlt = (n: number | null | undefined): string => (n == null || isNaN(n) ? 'N/A' : `${n.toFixed(1)}m`);
    return `${fmtCoord(lat)}, ${fmtCoord(lng)}, ${fmtAlt(alt)}`;
  }

  /**
   * V8 status chip — same look as the gateways/devices list pages: small
   * pill, uppercase tracking, dot on the left. Three tones (ok/warn/off) that
   * also flip cleanly in dark mode. Returns the full Tailwind class string so
   * the template stays a one-line {@code [class]} binding (no class
   * concatenation in the template, no extra dictionary lookups).
   */
  protected statusChipClass(s: string | null | undefined): string {
    const base =
      'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider';
    if (s === 'ONLINE') return `${base} bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300`;
    if (s === 'CHARGING') return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300`;
    if (s === 'OFFLINE') return `${base} bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300`;
    return `${base} bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300`;
  }

  protected statusDotClass(s: string | null | undefined): string {
    if (s === 'ONLINE') return 'bg-green-500';
    if (s === 'CHARGING') return 'bg-yellow-500';
    if (s === 'OFFLINE') return 'bg-red-500';
    return 'bg-gray-400 dark:bg-slate-500';
  }

  // ── États des gateways (gateway-live-state.ts) ────────────────────────
  /** Horloge pour « Reçu il y a … », avancée chaque seconde tant que le direct est fourni. */
  protected readonly now = signal(Date.now());
  protected readonly rawOpen = signal<Record<string, boolean>>({});

  protected readonly gatewayStates = computed(() => {
    const out = new Map<string, { stale: boolean; rows: GatewayStateRow[] }>();
    for (const g of this.sidebarGateways()) {
      const rows: GatewayStateRow[] = [
        { label: 'signal', reading: readSignalState(g.config?.stateSignal, g.isEmitting) },
        { label: 'autoSync', reading: readAutoSync(g.config?.pg) },
        { label: 'gpsdo', reading: readGpsdo(g.config?.pg) },
      ];
      out.set(g.id ?? '', {
        stale: g.status !== 'ONLINE',
        rows: rows.some(r => r.reading) ? rows : [],
      });
    }
    return out;
  });

  protected readonly gatewaySummary = computed(() => {
    const gws = this.sidebarGateways();
    const online = gws.filter(g => g.status === 'ONLINE');
    return {
      total: gws.length,
      parts: [
        { key: 'online', count: online.length },
        {
          key: 'inAir',
          count: online.filter(g => readSignalState(g.config?.stateSignal, g.isEmitting)?.tone === 'ok').length,
        },
        { key: 'locked', count: online.filter(g => readAutoSync(g.config?.pg)?.tone === 'ok').length },
      ],
    };
  });

  protected summaryDotClass(count: number, total: number): string {
    if (count === total) return 'bg-green-500';
    return count === 0 ? 'bg-red-500' : 'bg-yellow-400';
  }

  protected toneTextClass(tone: GatewayStateTone | 'stale'): string {
    return {
      ok: 'text-green-800 dark:text-green-300',
      wait: 'text-yellow-800 dark:text-yellow-300',
      ko: 'text-red-700 dark:text-red-300',
      off: 'text-gray-600 dark:text-slate-400',
      stale: 'text-gray-500 dark:text-slate-400',
    }[tone];
  }

  protected toneDotClass(tone: GatewayStateTone | 'stale'): string {
    return {
      ok: 'bg-green-500',
      wait: 'bg-yellow-400',
      ko: 'bg-red-500',
      off: 'bg-gray-400 dark:bg-slate-500',
      stale: 'border border-gray-400 dark:border-slate-500',
    }[tone];
  }

  /** Pied de carte : âge du dernier message reçu en direct, ou état chargé avec la page. */
  protected receivedText(g: Gateway): { key: string; age: string } | null {
    const at = this.gatewayLastMessageAt()[g.id ?? ''];
    const offline = g.status !== 'ONLINE';
    if (at != null) {
      const age = formatAge(this.now() - at);
      return { key: offline ? 'siteViewMap.state.lastKnownAgo' : 'siteViewMap.state.receivedAgo', age };
    }
    if (this.liveConnected() === null) return null;
    return { key: offline ? 'siteViewMap.state.lastKnown' : 'siteViewMap.state.atLoad', age: '' };
  }

  protected toggleRaw(id: string | undefined): void {
    if (!id) return;
    this.rawOpen.update(open => ({ ...open, [id]: !open[id] }));
  }

  protected rawBlocks(g: Gateway): Array<{ topic: string; json: string }> {
    const pretty = (raw: string) => {
      try {
        return JSON.stringify(JSON.parse(raw), null, 1);
      } catch {
        return raw;
      }
    };
    const out: Array<{ topic: string; json: string }> = [];
    if (g.config?.stateSignal) out.push({ topic: 'to_srv/signal_state', json: pretty(g.config.stateSignal) });
    if (g.config?.pg) out.push({ topic: 'to_srv/pg', json: pretty(g.config.pg) });
    return out;
  }

  /** Diff the new selection vs current subs and (un)subscribe accordingly. */
  protected onMobileSelectionChange(ids: string[]): void {
    this.selectedMobileIds.set(ids);
    const wanted = new Set(ids);

    // Unsubscribe trackers no longer selected and drop their last position.
    for (const [id, sub] of this.subs.entries()) {
      if (!wanted.has(id)) {
        sub.unsubscribe();
        this.subs.delete(id);
      }
    }
    this.livePositions.set(this.livePositions().filter(p => wanted.has(p.trackerId)));

    // Subscribe to new selections.
    for (const id of ids) {
      if (this.subs.has(id)) continue;
      this.subscribeToTracker(id);
    }
  }

  private async subscribeToTracker(id: string): Promise<void> {
    const ws = this.#ws;
    if (!ws) {
      // No websocket provided by the host app: the live layer is off, say it once per tracker
      // rather than failing silently (this is exactly how inside lost its live positions).
      console.warn(`[site-view-map] SITE_CONFIG_WEBSOCKET not provided — no live position for ${id}`);
      return;
    }
    try {
      await ws.initializeStomp();
    } catch {
      return;
    }
    // initializeStomp() resolves on *activate*, not on CONNECTED — subscribing in between throws
    // inside @stomp/stompjs and the subscription is lost for good. Wait for the real connection,
    // and bail out if the tracker was deselected (or the view destroyed) meanwhile.
    const ready = await waitForWebsocket(
      () => ws.isWebSocketConnected(),
      () => this.destroyed || !this.selectedMobileIds().includes(id)
    );
    if (!ready || this.subs.has(id)) return;

    const meta = this.mobileTrackers().find(t => t.id === id);
    const idx = this.mobileTrackers().findIndex(t => t.id === id);
    const colour = TRACKER_PALETTE[idx >= 0 ? idx % TRACKER_PALETTE.length : 0];
    const name = meta?.name ?? id;

    try {
      const sub = ws.subscribe<unknown>(`/topic/tracker/${id}/position`, msg => {
        const parsed = this.parsePosition(msg);
        if (!parsed) return;
        const next: LiveTrackerPosition = {
          trackerId: id,
          name,
          color: colour,
          latitude: parsed.latitude,
          longitude: parsed.longitude,
          altitude: parsed.altitude,
          timestamp: Date.now(),
          measuredAt: parsed.measuredAt,
        };
        const others = this.livePositions().filter(p => p.trackerId !== id);
        this.livePositions.set([...others, next]);
      });
      this.subs.set(id, sub);
    } catch (e) {
      console.warn('[site-view-map] subscribe failed for tracker %s', id, e);
    }
  }

  /** Position payload may be JSON (`{lat, lng}`) or a flat string `lat,lng`. Be permissive. */
  private parsePosition(
    msg: unknown
  ): { latitude: number; longitude: number; altitude?: number; measuredAt?: number } | null {
    try {
      let obj: Record<string, unknown> | null = null;
      if (typeof msg === 'string') {
        obj = JSON.parse(msg) as Record<string, unknown>;
      } else if (msg && typeof msg === 'object') {
        obj = msg as Record<string, unknown>;
      }
      if (!obj) return null;
      const lat = Number(obj['latitude'] ?? obj['lat']);
      const lng = Number(obj['longitude'] ?? obj['lng'] ?? obj['lon']);
      const alt = obj['altitude'] != null ? Number(obj['altitude']) : undefined;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        latitude: lat,
        longitude: lng,
        altitude: Number.isFinite(alt as number) ? (alt as number) : undefined,
        measuredAt: timestampMs(obj['timestamp']),
      };
    } catch {
      return null;
    }
  }
}

interface GatewayStateRow {
  label: 'signal' | 'autoSync' | 'gpsdo';
  reading: GatewayStateReading | null;
}

/** « 12 s », « 3 min », « 2 h 05 » : unités communes au français et à l'anglais. */
function formatAge(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  return `${Math.floor(s / 3600)} h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
}
